import {
  SessionBooking,
  SessionBookingLinkType,
  SessionBillingPeriod,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { useSessionBookingModalStore } from "./useSessionBookingModalStore"
import useSessionLateCancelModalStore from "./useSessionLateCancelModalStore"
import { useSessionMakeUpVerificationModalStore } from "./useSessionMakeUpVerificationModalStore"
import useSessionModalStore from "./useSessionModalStore"
import { useSessionNoShowModalStore } from "./useSessionNoShowModalStore"
import {
  useSessionBillingPeriodsQuery,
  useSessionBookingsQuery,
} from "../../../../networking/sessions/queries"
import { ANALYTIC_EVENTS } from "../../../../util/analytics"
import { trackAnalyticEvent } from "../../../../util/analytics"
import { localStorageItems } from "../../../../util/storage"
import { ClientTab } from "../../ClientTabNav"
import useCurrentClient from "../../hooks/useCurrentClient"

const useSession = (session: SessionBooking | undefined) => {
  const { onOpen } = useSessionBookingModalStore()
  const { onOpen: onOpenNoShowModal } = useSessionNoShowModalStore()
  const { onOpen: onOpenLateCancelModal } = useSessionLateCancelModalStore()
  const { onOpen: onOpenMakeUpVerificationModal } =
    useSessionMakeUpVerificationModalStore()
  const { data: billingPeriods } = useSessionBillingPeriodsQuery()
  const { data: bookings } = useSessionBookingsQuery()
  const { onOpen: openSessionModal } = useSessionModalStore()

  const navigate = useNavigate()
  const { currentCoachingGroup } = useCurrentClient()

  const handleReschedule = useCallback(() => {
    if (!session?.reschedule_link) return
    onOpen(session?.reschedule_link, SessionBookingLinkType.RESCHEDULE)
    trackAnalyticEvent(ANALYTIC_EVENTS.RESCHEDULE_COACH_SESSION, {
      auto_scheduled: session?.auto_scheduled,
    })
    localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
  }, [session, onOpen])

  const handleSchedule = useCallback(
    (billingPeriod: SessionBillingPeriod) => {
      if (!billingPeriod?.coach_booking_link) return
      onOpen(
        billingPeriod.coach_booking_link,
        SessionBookingLinkType.COACH_BOOKING
      )
      trackAnalyticEvent(ANALYTIC_EVENTS.SCHEDULE_COACH_SESSION, {})
      localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
    },
    [session, onOpen]
  )

  const handleJoinSession = () => {
    if (!session?.meeting_link) return
    window.open(session?.meeting_link, "_blank")
  }

  const handleCancelSession = () => {
    if (!session?.cancel_link) return
    onOpen(session?.cancel_link, SessionBookingLinkType.CANCEL)
    trackAnalyticEvent(ANALYTIC_EVENTS.CANCEL_COACH_SESSION, {
      auto_scheduled: session?.auto_scheduled,
    })
    localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
  }

  const handleNoShowSession = () => {
    if (!session?.id) return
    onOpenNoShowModal(session?.id)
  }

  const handleScheduleMakeUp = (billingPeriod: SessionBillingPeriod) => {
    if (!session?.id) return
    // If has not verified with Admin, display makeup verification modal
    if (!session.allow_makeup) {
      onOpenMakeUpVerificationModal(
        session?.id,
        billingPeriod?.makeup_booking_link
      )
    } else {
      // Otherwise, open the makeup booking link
      if (!billingPeriod?.makeup_booking_link) return
      onOpen(
        billingPeriod?.makeup_booking_link,
        SessionBookingLinkType.MAKEUP_BOOKING
      )
      trackAnalyticEvent(ANALYTIC_EVENTS.MAKEUP_COACH_SESSION, {
        auto_scheduled: session?.auto_scheduled,
      })
      localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
    }
  }

  const handleViewNotes = () => {
    if (!session?.id) return
    openSessionModal(session?.id)
    navigate(`/${currentCoachingGroup?.id}/${ClientTab.SESSIONS}/`)
  }

  const handleLateCancelSession = () => {
    if (!session?.id) return
    onOpenLateCancelModal(session?.id)
  }

  const sessionIsWithin24Hours = useMemo(
    () =>
      dayjs(session?.start_datetime).isBefore(dayjs().add(24, "hours")) &&
      dayjs(session?.end_datetime).isAfter(dayjs()),
    [session?.start_datetime, session?.end_datetime]
  )

  const sessionIsWithin48Hours = useMemo(
    () =>
      dayjs(session?.start_datetime).isBefore(dayjs().add(48, "hours")) &&
      dayjs(session?.end_datetime).isAfter(dayjs()),
    [session?.start_datetime, session?.end_datetime]
  )

  const sessionIsPast = useMemo(
    () => dayjs(session?.end_datetime).isBefore(dayjs()),
    [session?.end_datetime]
  )

  const sessionStartedMoreThan15MinutesAgo = useMemo(
    () =>
      dayjs(session?.start_datetime).isBefore(dayjs().subtract(15, "minutes")),
    [session?.start_datetime]
  )

  const sessionBillingPeriod = useMemo(
    () =>
      billingPeriods?.find((period) =>
        period.bookings?.some((booking) => booking.id === session?.id)
      ),
    [billingPeriods, session?.id]
  )

  const hasMakeupScheduled = useMemo(
    () =>
      bookings?.some((booking) => booking.missed_session_id === session?.id),
    [bookings, session?.id]
  )

  const isBooked = !!session
  const showScheduleMakeUpButton =
    (session?.marked_no_show || session?.marked_late_cancellation) &&
    !hasMakeupScheduled
  const showResheduleButton =
    !sessionIsPast &&
    isBooked &&
    !session?.marked_late_cancellation &&
    !session?.marked_no_show
  const showJoinButton = sessionIsWithin24Hours && !session?.marked_no_show
  const showCancelButton =
    !sessionIsPast &&
    !sessionIsWithin24Hours &&
    !session?.makeup_session_id &&
    !session?.marked_no_show &&
    !session?.marked_late_cancellation
  const showNoShowButton =
    sessionStartedMoreThan15MinutesAgo &&
    !session?.makeup_session_id &&
    !session?.marked_no_show &&
    !session?.marked_late_cancellation
  const showLateCancelButton =
    sessionIsWithin24Hours &&
    !session?.makeup_session_id &&
    !session?.marked_no_show &&
    !session?.marked_late_cancellation

  const sessionDate = isBooked ? dayjs(session?.start_datetime) : undefined
  const sessionIsTomorrow = sessionDate?.isSame(dayjs().add(1, "day"), "day")
  const sessionIsUpcoming = sessionIsWithin24Hours || sessionIsTomorrow

  return {
    handleReschedule,
    handleSchedule,
    handleJoinSession,
    handleViewNotes,
    handleCancelSession,
    handleNoShowSession,
    handleLateCancelSession,
    handleScheduleMakeUp,
    sessionIsWithin24Hours,
    sessionIsWithin48Hours,
    sessionIsPast,
    sessionBillingPeriod,
    hasMakeupScheduled,
    isBooked,
    showScheduleMakeUpButton,
    showResheduleButton,
    showJoinButton,
    showCancelButton,
    showNoShowButton,
    showLateCancelButton,
    sessionIsUpcoming,
  }
}

export default useSession
